import { Form, Formik } from 'formik';
import { useGetQuickVideo } from 'lib/api/quickVideo/useGetQuickVideo';
import { LoadingIndicator } from 'lib/components';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import { ParagraphNormalBold } from 'lib/components/styles/typography';
import React from 'react';
import { Button } from 'react-covideo-common';
import { MdClose, MdSend } from 'react-icons/md';
import { useHistory, useParams } from 'react-router';
import { getQuickVideoTestSendInitialValues } from '../helpers/getQuickVideoTestSendInitialValues';
import { useAuth } from 'lib/context';
import { Gap } from 'lib/components/styles/layout';
import { useGetConfiguration } from 'lib/api/quickVideo/useGetConfiguration';
import { groupBy, omit } from 'lodash';
import { testSendValidation } from '../helpers/testSendValidation';
import {
  IQuickVideoPartTestSendForm,
  IQuickVideoTestSendForm,
  QuickVideoPartType,
} from 'lib/api/quickVideo/types';
import {
  ITestSendQuickVideoPart,
  useTestSendMutation,
} from 'lib/api/quickVideo/useTestSendMutation';
import { CgSpinnerTwoAlt } from 'react-icons/cg';
import { NotFound } from 'app/pages/notFound/NotFound';
import { TestSendForm } from '../components/forms/TestSendForm';
import { useTranslation } from 'react-i18next';

export const TestSendModal = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { quickVideoId } = useParams<{ quickVideoId: string }>();
  const history = useHistory();

  const { data: quickVideo, isLoading: isQuickVideoLoading } = useGetQuickVideo(
    {
      quickVideoId: Number(quickVideoId) || 0,
      enabled: !!quickVideoId,
    }
  );

  const { data: configurationData, isLoading: isConfigurationLoading } =
    useGetConfiguration();

  const positions = configurationData?.positions || [];
  const allVideoPartOptions = groupBy(configurationData?.parts || [], 'type');

  const { mutateAsync: testSendMutation, isLoading: isSending } =
    useTestSendMutation({
      onSuccess: () => {
        history.push('/ai-configuration/quick-videos');
      },
    });

  const isLoading = isQuickVideoLoading || isConfigurationLoading;

  const onSubmit = async (values: IQuickVideoTestSendForm) => {
    const quickVideoParts: ITestSendQuickVideoPart[] = values.quickVideoParts
      .filter(
        (
          part
        ): part is IQuickVideoPartTestSendForm & {
          partType: QuickVideoPartType;
        } => !!part.partType
      ) // needed because we allow partType to be empty in form but not for submitting
      .map(part => {
        if (!!part.video) {
          return {
            quickVideoPartId: part.quickVideoPartId,
            videoId: Number(part.video.id),
          };
        }
        return {
          quickVideoPartId: part.quickVideoPartId,
        };
      });

    const submissionData = {
      ...omit(values, 'recipientEmail'),
      quickVideoParts,
    };

    await testSendMutation({
      ...submissionData,
      quickVideoId: Number(quickVideoId),
    });
  };

  if (isLoading) {
    return (
      <FullScreenModal>
        <LoadingIndicator height={'100%'} isLoading />
      </FullScreenModal>
    );
  }

  if (!quickVideo) {
    return <NotFound />;
  }

  return (
    <Formik
      initialValues={getQuickVideoTestSendInitialValues({
        userData,
        quickVideo,
        positions,
      })}
      validationSchema={testSendValidation}
      validateOnMount
      onSubmit={onSubmit}
    >
      {({ isValid, handleSubmit }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <FullScreenModal
              headerLeft={
                <Button
                  icon={<MdClose size={18} />}
                  variant='transparent'
                  disabled={isSending}
                  onClick={() => {
                    history.push(`/ai-configuration/quick-videos`);
                  }}
                />
              }
              headerCenter={
                <ParagraphNormalBold>
                  {t('quickVideoTestSend', { name: quickVideo?.name || '' })}
                </ParagraphNormalBold>
              }
              headerRight={
                <Button
                  text={t('Send Video')}
                  icon={isSending ? <CgSpinnerTwoAlt /> : <MdSend />}
                  iconPosition='right'
                  loading={isSending}
                  disabled={!isValid || isSending}
                  type='submit'
                />
              }
            >
              <Gap
                justifyContent='center'
                style={{
                  padding: '20px 0',
                }}
              >
                <TestSendForm
                  quickVideo={quickVideo}
                  allVideoPartOptions={allVideoPartOptions}
                  positions={positions}
                />
              </Gap>
            </FullScreenModal>
          </Form>
        );
      }}
    </Formik>
  );
};
